import { Button } from "@/app/(components)/Basic/Button";
import { Type } from "@/app/(components)/Basic/Button/constants";
import { FormInput } from "@/app/(components)/Basic/FormInput";
import { Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import { getSignedArtOrderExtendMessage } from "@/eip712";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArtOrderFull,
  ArtOrderOffer,
  ArtOrderTimeframeUpdate,
} from "@/api/art-order/types";
import artOrderApi from "@/api/art-order/order/client";
import useSignedFunction from "@/shared/hooks/useSignedFunction";
import useEIP712Signature from "@/eip712/hooks/useEIP712Signature";
import { addDays } from "date-fns";
import { toSeconds } from "@/shared/dates";

export interface Props {
  artOrder: ArtOrderFull;
  acceptedOffer: ArtOrderOffer;
  onSuccess: () => void;
}

const ExtendOrderForm = ({ artOrder, acceptedOffer, onSuccess }: Props) => {
  const queryClient = useQueryClient();

  const validationSchema = Yup.object().shape({
    timeframe: Yup.number().required("Extend for field is required"),
  });

  const sendMessageForSign = useEIP712Signature({
    onError: async () => {
      await artOrderApi.extendArtOrder(artOrder.id, artOrder.deadline);
    },
  });

  const { mutateAsync: requestSignature, isLoading: isSignatureLoading } =
    useMutation({
      mutationFn: async (order: ArtOrderFull) => {
        const offerTypedData = getSignedArtOrderExtendMessage(
          order,
          acceptedOffer,
        );

        const signature = await sendMessageForSign(offerTypedData);

        await artOrderApi.updateOrderSignature(artOrder.id, signature);
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries(["art-order", artOrder.id]);
      },
    });

  const { mutateAsync: extendOrder, isLoading: isCreateLoading } = useMutation({
    mutationFn: (order: ArtOrderTimeframeUpdate) =>
      artOrderApi.extendArtOrder(order.id, order.deadline),
    onSuccess: (order: any) => {
      requestSignature(order);
    },
    onError: () => {},
  });

  const checkedExtendOrder = useSignedFunction(extendOrder);

  const onSubmit = async (values: any) => {
    await checkedExtendOrder({
      id: artOrder.id,
      deadline: toSeconds(addDays(Date.now(), values.timeframe)),
    });
  };

  return (
    <Formik
      initialValues={{
        timeframe: undefined,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <FormInput
            name="timeframe"
            type="number"
            label="Extend for"
            alignCenter={false}
            disabled={isSubmitting}
            endInfo="Days"
          />
          <Button
            element={Type.Button}
            type="submit"
            disabled={isCreateLoading || isSignatureLoading}
            className="flex mt-6 mx-auto"
            endIcon={
              (isCreateLoading || isSignatureLoading) && (
                <ArrowRightIcon className="w-4" />
              )
            }
          >
            {isCreateLoading || isSignatureLoading
              ? "Loading"
              : "Extend your order"}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default ExtendOrderForm;
